import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

/**
 * Window event every task write dispatches once it succeeds (#904). The write
 * helpers below never touch React Query: `bindTaskQueries` (bound once in
 * `App()`) is the only listener that invalidates, so a write from any screen
 * refreshes the Tasks page and the sidebar badge without a reload, and nothing
 * that writes a task needs a QueryClientProvider.
 */
export const TASKS_CHANGED_EVENT = 'ncrm:tasks-changed';

/** Tell the app a task was created, changed or removed. */
export function notifyTasksChanged() {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new Event(TASKS_CHANGED_EVENT));
}

/** Listen for `notifyTasksChanged`; returns the unsubscribe. */
export function onTasksChanged(handler) {
    if (typeof window === 'undefined') return () => {};
    window.addEventListener(TASKS_CHANGED_EVENT, handler);
    return () => window.removeEventListener(TASKS_CHANGED_EVENT, handler);
}

/** Refetch the task lists and the summary behind the badge on every task write. */
export function bindTaskQueries(queryClient) {
    return onTasksChanged(() => {
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
        queryClient.invalidateQueries({ queryKey: ['tasksSummary'] });
    });
}

// A rejection (or a synchronous throw) from the request skips the notification.
async function written(request) {
    const res = await request();
    notifyTasksChanged();
    return res.data;
}

/** POST /activities with a built task body (`type`, `status` and the fields). */
export const createTask = (task, headers = {}) => written(
    () => axios.post(`${API}/activities`, task, { headers }),
);

/** Complete a task from any open status; also advances a campaign step. */
export const completeTask = (activityId, headers = {}) => written(
    () => axios.put(`${API}/activities/${activityId}/complete`, {}, { headers }),
);

/** Reopen a task from any closed status. */
export const reopenTask = (activityId, headers = {}) => written(
    () => axios.put(`${API}/activities/${activityId}/reopen`, {}, { headers }),
);

export const updateTask = (activityId, updates, headers = {}) => written(
    () => axios.put(`${API}/activities/${activityId}`, updates, { headers }),
);

export const deleteTask = (activityId, headers = {}) => written(
    () => axios.delete(`${API}/activities/${activityId}`, { headers }),
);

export const bulkCompleteTasks = (activityIds, headers = {}) => written(
    () => axios.post(`${API}/activities/bulk-complete`, { activity_ids: activityIds }, { headers }),
);

export const bulkDeleteTasks = (activityIds, headers = {}) => written(
    () => axios.post(`${API}/activities/bulk-delete`, { activity_ids: activityIds }, { headers }),
);

/** Reassign a task using the server's compare-and-swap owner contract. */
export const reassignTask = (activityId, { owner_id, expected_owner_id } = {}, headers = {}) => written(
    () => axios.put(`${API}/activities/${activityId}/assign`, { owner_id, expected_owner_id }, { headers }),
);

export { API as TASKS_API };
